import { useState, useRef, useEffect, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useQuery } from "convex/react";
import { useAuthActions } from "@convex-dev/auth/react";
import { motion } from "framer-motion";
import {
  Calendar,
  CalendarDays,
  Inbox,
  LogOut,
  Settings as SettingsIcon,
} from "lucide-react";
import { api } from "@convex/_generated/api";
import HeaderRecorder from "./HeaderRecorder";

const navItems = [
  { to: "/", label: "Calendar", end: true, icon: Calendar },
  { to: "/schedule", label: "Schedule", end: false, icon: CalendarDays },
  { to: "/unassigned", label: "Unassigned", end: false, icon: Inbox },
  { to: "/settings", label: "Settings", end: false, icon: SettingsIcon },
];

export default function Layout({ children }: { children: ReactNode }) {
  const { signOut } = useAuthActions();
  const viewer = useQuery(api.users.viewer);
  const location = useLocation();
  const [isEmailRevealed, setIsEmailRevealed] = useState(false);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emailRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    return () => {
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
      }
    };
  }, []);

  const handleEmailClick = (e: React.MouseEvent) => {
    // Multi-click (e.g. double click) cancels any pending blur and keeps email revealed
    if (e.detail > 1) {
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
      return;
    }

    if (!isEmailRevealed) {
      setIsEmailRevealed(true);
    } else {
      // Delay blurring to allow double-click without accidental re-blur
      if (blurTimerRef.current) {
        clearTimeout(blurTimerRef.current);
      }
      blurTimerRef.current = setTimeout(() => {
        const hasSelection = !!window.getSelection()?.toString().trim();
        if (!hasSelection) {
          setIsEmailRevealed(false);
        }
        blurTimerRef.current = null;
      }, 250);
    }
  };

  const handleEmailDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
    setIsEmailRevealed(true);
    if (emailRef.current) {
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(emailRef.current);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsEmailRevealed((prev) => !prev);
    }
  };

  return (
    <div className="flex h-full flex-col bg-[#fdfdfc] dark:bg-[#0f0f0e] text-[#222] dark:text-[#ededeb] font-sans">
      <header className="no-print relative z-30 flex h-14 items-center justify-between gap-3 sm:gap-6 border-b border-[#d1d1cf] bg-[#fdfdfc]/90 px-3.5 sm:px-10 backdrop-blur-xs dark:border-[#2a2a28] dark:bg-[#0f0f0e]/90 transition-colors">
        <div className="flex items-center gap-3 sm:gap-4 shrink-0">
          <div className="flex items-baseline gap-2">
            <span className="text-base font-light tracking-[-0.05em] text-[#222] dark:text-[#ededeb]">
              LLS
            </span>
            <span className="hidden sm:inline font-mono text-[10px] tracking-wider text-neutral-400 uppercase opacity-70">
              Lecture listening scheme
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden sm:flex h-7 items-center gap-1.5 ml-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  [
                    "relative flex h-7 items-center justify-center rounded-xl px-3 text-xs font-medium transition-colors duration-150",
                    isActive
                      ? "font-semibold text-[#222] dark:text-white"
                      : "text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100",
                  ].join(" ")
                }
              >

                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span
                        layoutId="main-nav-pill"
                        className="absolute inset-0 rounded-xl bg-neutral-900/[0.08] dark:bg-white/[0.12]"
                        transition={{ type: "spring", stiffness: 450, damping: 35 }}
                      />
                    )}
                    <span className="relative z-10">{item.label}</span>
                  </>
                )}
              </NavLink>
            ))}
            <div className="mx-1 h-3.5 w-[1px] bg-neutral-200 dark:bg-neutral-800" />
            <HeaderRecorder />
          </nav>
        </div>

        {/* On Mobile: HeaderRecorder in the center/right */}
        <div className="flex sm:hidden items-center gap-2">
          <HeaderRecorder />
        </div>

        {/* User / Sign out area */}
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <span
            ref={emailRef}
            role="button"
            tabIndex={0}
            onClick={handleEmailClick}
            onDoubleClick={handleEmailDoubleClick}
            onKeyDown={handleEmailKeyDown}
            title={isEmailRevealed ? "Click to blur email (double-click to select)" : "Click to reveal email"}
            className={`hidden md:inline cursor-pointer font-mono text-xs transition-all duration-200 ${
              isEmailRevealed
                ? "opacity-60 blur-none text-neutral-700 dark:text-neutral-300 select-text"
                : "opacity-40 blur-xs text-neutral-700 dark:text-neutral-300 hover:opacity-60 select-none"
            }`}
          >
            {viewer?.username ?? viewer?.email ?? ""}
          </span>
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.96 }}
            onClick={() => void signOut()}
            title="Sign out"
            className="flex h-7 cursor-pointer items-center gap-1.5 rounded-xl border border-[#d1d1cf] px-2.5 text-xs font-medium text-neutral-700 transition-all hover:border-neutral-400 hover:bg-[#fafaf8] dark:border-[#2a2a28] dark:text-neutral-300 dark:hover:border-neutral-600 dark:hover:bg-[#171716]"
          >

            <LogOut className="h-3.5 w-3.5 sm:hidden" />
            <span className="hidden sm:inline">Sign out</span>
          </motion.button>
        </div>

        {/* Meniscus bottom line */}
        <div className="meniscus absolute bottom-0 left-0 w-full h-[1px] opacity-40 pointer-events-none" />
      </header>

      <main className="print-flow min-h-0 flex-1 overflow-auto px-3.5 py-4 pb-24 sm:px-10 sm:py-8 sm:pb-8">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          className="h-full"
        >
          {children}
        </motion.div>
      </main>

      {/* Mobile Bottom Navigation Bar */}
      <nav className="no-print sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#fdfdfc]/95 dark:bg-[#0f0f0e]/95 backdrop-blur-md border-t border-[#d1d1cf] dark:border-[#2a2a28] pb-safe transition-colors">
        <div className="meniscus absolute top-0 left-0 w-full h-[1px] opacity-40 pointer-events-none" />
        <div className="grid grid-cols-4 h-14 items-center px-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  [
                    "relative flex flex-col items-center justify-center h-full py-1 transition-colors duration-150",
                    isActive
                      ? "font-semibold text-neutral-900 dark:text-white"
                      : "text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200",
                  ].join(" ")
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span
                        layoutId="mobile-nav-pill"
                        className="absolute inset-x-2 inset-y-1 rounded-xl bg-neutral-900/[0.08] dark:bg-white/[0.12]"
                        transition={{ type: "spring", stiffness: 450, damping: 35 }}
                      />
                    )}
                    <Icon className="relative z-10 h-4 w-4" />
                    <span className="relative z-10 text-[10px] tracking-tight mt-0.5">{item.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
